import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  PiggyBank,
  Plus,
  Search,
  Target,
  Trash2,
  WalletCards,
  TrendingUp,
} from "lucide-react";
import {
  excluirCofrinho,
  movimentarCofrinho,
  salvarCofrinho,
} from "../services/financeService";
import { formatarMoeda, moedaParaNumero } from "../utils/money";
import "../styles/pages/metas.css";

const iconesBasicos = [
  "🎯", "🚗", "🏠", "✈️", "💻", "📱",
  "🎮", "💰", "🏍️", "📚", "🛒", "❤️",
];

const iconesExtras = [
  "🏦", "🏡", "🏢", "🧱", "🔑", "🛋️",
  "🚙", "🏎️", "⛽", "🛞", "🛵", "🚲",
  "🌍", "🧳", "🏖️", "⛰️", "🛫", "🛳️",
  "📈", "📊", "💳", "💵", "🪙", "💎",
  "🎓", "🧠", "📖", "🧑‍💻", "🖥️", "⌨️",
  "🏋️", "⚽", "🎬", "🎧", "🎹", "📷",
  "👶", "🐶", "🐱", "💊", "🏥", "🦷",
  "🍔", "🍕", "☕", "🥩", "🥗", "🧁",
];

function diasAtePrazo(prazo) {
  if (!prazo) return null;
  const hoje = new Date();
  const fim = new Date(`${prazo}T12:00:00`);
  const diff = fim.getTime() - hoje.getTime();
  return Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 0);
}

export default function Metas({ uid, resumo, cofrinhos = [] }) {
  const [nome, setNome] = useState("");
  const [icone, setIcone] = useState("🎯");
  const [meta, setMeta] = useState("");
  const [valorAtual, setValorAtual] = useState("");
  const [prazo, setPrazo] = useState("");
  const [busca, setBusca] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [movimentandoId, setMovimentandoId] = useState("");
  const [valorMovimento, setValorMovimento] = useState("");
  const [mostrarMaisIcones, setMostrarMaisIcones] = useState(false);

  const iconesVisiveis = mostrarMaisIcones
    ? [...iconesBasicos, ...iconesExtras]
    : iconesBasicos;

  const totalGuardado = useMemo(() => {
    return cofrinhos.reduce((acc, item) => acc + Number(item.valorAtual || 0), 0);
  }, [cofrinhos]);

  const totalObjetivos = useMemo(() => {
    return cofrinhos.reduce((acc, item) => acc + Number(item.meta || 0), 0);
  }, [cofrinhos]);

  const percentualGeral =
    totalObjetivos > 0 ? Math.min((totalGuardado / totalObjetivos) * 100, 100) : 0;

  const metasFiltradas = useMemo(() => {
    return cofrinhos.filter((item) =>
      `${item.nome || ""}`.toLowerCase().includes(busca.toLowerCase())
    );
  }, [cofrinhos, busca]);

  async function criarCofrinho(e) {
    e.preventDefault();
    setErro("");

    if (!nome.trim()) {
      setErro("Informe o nome do cofrinho.");
      return;
    }

    try {
      setSalvando(true);

      await salvarCofrinho(uid, {
        nome,
        icone,
        meta: moedaParaNumero(meta),
        valorAtual: moedaParaNumero(valorAtual),
        prazo,
      });

      setNome("");
      setIcone("🎯");
      setMeta("");
      setValorAtual("");
      setPrazo("");
      setMostrarMaisIcones(false);
    } catch (error) {
      console.error(error);
      setErro(error.message || "Erro ao criar cofrinho.");
    } finally {
      setSalvando(false);
    }
  }

  async function aplicarMovimento(cofrinhoId, tipo) {
    setErro("");

    try {
      await movimentarCofrinho(uid, cofrinhoId, {
        tipo,
        valor: moedaParaNumero(valorMovimento),
      });

      setMovimentandoId("");
      setValorMovimento("");
    } catch (error) {
      console.error(error);
      setErro(error.message || "Erro ao movimentar cofrinho.");
    }
  }

  return (
    <section className="goal-page">
      <div className="goal-header">
        <div>
          <span>Planejamento</span>
          <h2>Metas e cofrinhos</h2>
          <p>Construa objetivos financeiros e acompanhe sua evolução.</p>
        </div>

        <div className="goal-header-icon">
          <PiggyBank size={24} />
        </div>
      </div>

      <div className="goal-summary-grid">
        <SummaryCard type="green" icon={<WalletCards size={22} />} label="Total guardado" value={formatarMoeda(totalGuardado)} />
        <SummaryCard type="purple" icon={<Target size={22} />} label="Objetivos" value={cofrinhos.length} />
        <SummaryCard type="blue" icon={<TrendingUp size={22} />} label="Progresso" value={`${Math.round(percentualGeral)}%`} />
      </div>

      <section className="goal-form-card">
        <div className="goal-form-header">
          <div>
            <h2>Novo cofrinho</h2>
            <p>Crie metas para organizar seus sonhos financeiros.</p>
          </div>

          <div className="goal-form-icon">
            <Plus size={22} />
          </div>
        </div>

        <form className="goal-form" onSubmit={criarCofrinho}>
          <div className="goal-field full">
            <label>Nome</label>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Reserva de emergência"
            />
          </div>

          <div className="goal-field goal-icon-field">
            <label>Ícone</label>

            <div className={mostrarMaisIcones ? "goal-icons-grid expanded" : "goal-icons-grid"}>
              {iconesVisiveis.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={icone === item ? "goal-icon-option active" : "goal-icon-option"}
                  onClick={() => setIcone(item)}
                >
                  {item}
                </button>
              ))}
            </div>

            <button
              className="goal-icons-more-btn"
              type="button"
              onClick={() => setMostrarMaisIcones((atual) => !atual)}
            >
              {mostrarMaisIcones ? (
                <>
                  <ChevronUp size={17} />
                  Ver menos ícones
                </>
              ) : (
                <>
                  <ChevronDown size={17} />
                  Ver mais ícones
                </>
              )}
            </button>
          </div>

          <div className="goal-field">
            <label>Meta</label>
            <input
              value={meta}
              onChange={(e) => setMeta(e.target.value)}
              placeholder="3000,00"
              inputMode="decimal"
            />
          </div>

          <div className="goal-field">
            <label>Já tenho</label>
            <input
              value={valorAtual}
              onChange={(e) => setValorAtual(e.target.value)}
              placeholder="0,00"
              inputMode="decimal"
            />
          </div>

          <div className="goal-field">
            <label>Prazo</label>
            <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
          </div>

          {erro && <div className="goal-error">{erro}</div>}

          <button className="goal-submit-btn" type="submit" disabled={salvando}>
            <Plus size={18} />
            {salvando ? "Salvando..." : "Criar cofrinho"}
          </button>
        </form>
      </section>

      <section className="goal-list-card">
        <div className="goal-list-header">
          <div>
            <h2>Meus objetivos</h2>
            <span>{metasFiltradas.length} objetivo(s) encontrado(s).</span>
          </div>

          <label className="goal-search">
            <Search size={16} />
            <input
              placeholder="Buscar objetivo..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </label>
        </div>

        <div className="goal-list">
          {metasFiltradas.length === 0 && (
            <div className="goal-empty">Nenhum cofrinho criado ainda.</div>
          )}

          {metasFiltradas.map((item) => {
            const metaNumero = Number(item.meta || 0);
            const atual = Math.max(Number(item.valorAtual || 0), 0);
            const percentual =
              metaNumero > 0 ? Math.min((atual / metaNumero) * 100, 100) : 0;
            const falta = Math.max(metaNumero - atual, 0);
            const dias = diasAtePrazo(item.prazo);
            const porDia = dias && dias > 0 ? falta / dias : 0;

            return (
              <article className="goal-item" key={item.id}>
                <div className="goal-item-top">
                  <div className="goal-item-left">
                    <div className="goal-item-icon">{item.icone || "🎯"}</div>

                    <div>
                      <strong>{item.nome}</strong>
                      <span>{formatarMoeda(atual)} de {formatarMoeda(metaNumero)}</span>
                    </div>
                  </div>

                  <button
                    className="goal-delete-btn"
                    onClick={() => excluirCofrinho(uid, item.id)}
                    type="button"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="goal-progress">
                  <div className="goal-progress-bar" style={{ width: `${percentual}%` }} />
                </div>

                <div className="goal-bottom">
                  <span>
                    Faltam {formatarMoeda(falta)}
                    {dias !== null && dias > 0 ? ` • ${formatarMoeda(porDia)} por dia` : ""}
                  </span>

                  <strong>{Math.round(percentual)}%</strong>
                </div>

                {movimentandoId === item.id ? (
                  <div className="goal-move-box">
                    <input
                      value={valorMovimento}
                      onChange={(e) => setValorMovimento(e.target.value)}
                      placeholder="Valor"
                      inputMode="decimal"
                    />

                    <button
                      className="goal-secondary-btn"
                      type="button"
                      onClick={() => aplicarMovimento(item.id, "retirar")}
                    >
                      Retirar
                    </button>

                    <button
                      className="goal-primary-mini"
                      type="button"
                      onClick={() => aplicarMovimento(item.id, "guardar")}
                    >
                      Guardar
                    </button>
                  </div>
                ) : (
                  <button
                    className="goal-secondary-wide"
                    type="button"
                    onClick={() => setMovimentandoId(item.id)}
                  >
                    Movimentar cofrinho
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </section>
  );
}

function SummaryCard({ type, icon, label, value }) {
  return (
    <article className={`goal-summary-card ${type}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>

      <div className="goal-card-icon">{icon}</div>
    </article>
  );
}
